import React, { useEffect, useMemo, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Avatar,
  AvatarFallback,
  AvatarImage,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "./components/ui";
import { cn } from "./lib/utils";
import {
  LayoutDashboard,
  FileText,
  Undo2,
  PackageSearch,
  LogOut,
  ChevronDown,
  Folder
} from "lucide-react";

const NAV_ITEMS = [
  { key: "dashboard", to: "/", icon: LayoutDashboard },
  { key: "invoices", to: "/invoices", icon: FileText },
  { key: "returns", to: "/returns", icon: Undo2 },
  { key: "orders", to: "/orders", icon: PackageSearch },
  { key: "files", to: "/files", icon: Folder }
];

const LANGUAGE_OPTIONS = [
  {
    value: "en",
    flag: "🇬🇧",
    labels: { en: "English", tr: "İngilizce" }
  },
  {
    value: "tr",
    flag: "🇹🇷",
    labels: { en: "Turkish", tr: "Türkçe" }
  }
];

const TRANSLATIONS = {
  en: {
    nav: {
      dashboard: "Dashboard",
      invoices: "Invoices",
      returns: "Returns",
      orders: "Orders",
      files: "Files"
    },
    beta: "beta",
    selectMarketplace: "Select marketplace",
    currentMarketplacePrefix: "Current:",
    account: "Account",
    connectedMarketplaces: "Connected marketplaces",
    noMarketplaces: "No marketplaces are connected.",
    chromeConnected: "Chrome extension connected",
    chromeDisconnected: "Chrome extension not detected",
    settings: "Settings",
    connectMarketplaces: "Connect marketplaces",
    logOut: "Log out",
    language: "Language",
    noMarketplaceSelected: "No marketplace selected",
    extensionStatusConnected: "Connected",
    extensionStatusDisconnected: "Not connected"
  },
  tr: {
    nav: {
      dashboard: "Kontrol Paneli",
      invoices: "Faturalar",
      returns: "İadeler",
      orders: "Siparişler",
      files: "Dosyalar"
    },
    beta: "beta",
    selectMarketplace: "Pazar yeri seç",
    currentMarketplacePrefix: "Geçerli:",
    account: "Hesap",
    connectedMarketplaces: "Bağlı pazar yerleri",
    noMarketplaces: "Bağlı pazar yeri yok.",
    chromeConnected: "Chrome uzantısı bağlı",
    chromeDisconnected: "Chrome uzantısı bulunamadı",
    settings: "Ayarlar",
    connectMarketplaces: "Pazar yerlerini bağla",
    logOut: "Çıkış yap",
    language: "Dil",
    noMarketplaceSelected: "Pazar yeri seçilmedi",
    extensionStatusConnected: "Bağlı",
    extensionStatusDisconnected: "Bağlı değil"
  }
};

const Topbar = ({
  handleLogout,
  marketplaces = [],
  selectedMarketplace,
  setSelectedMarketplace,
  username,
  role,
  language = "en",
  onLanguageChange
}) => {
  const [extensionAvailable, setExtensionAvailable] = useState(false);
  const navigate = useNavigate();

    useEffect(() => {
    const handleExtensionReady = () => setExtensionAvailable(true);

    const checkExtension = () => {
      try {
        const attr = document.documentElement?.getAttribute("data-nestos-invoice-extension");
        setExtensionAvailable(attr === "ready");
      } catch {
        setExtensionAvailable(false);
            }
        };

    checkExtension();
    window.addEventListener("nestosInvoiceExtensionReady", handleExtensionReady);
    document.addEventListener("nestosInvoiceExtensionReady", handleExtensionReady);
    const intervalId = setInterval(checkExtension, 5000);

        return () => {
      window.removeEventListener("nestosInvoiceExtensionReady", handleExtensionReady);
      document.removeEventListener("nestosInvoiceExtensionReady", handleExtensionReady);
      clearInterval(intervalId);
        };
  }, []);

  const marketplaceOptions = useMemo(() => marketplaces || [], [marketplaces]);
  const hasMarketplaces = marketplaceOptions.length > 0;
  const currentMarketplace = hasMarketplaces
    ? marketplaceOptions.find((m) => m.country_code === selectedMarketplace) || marketplaceOptions[0]
    : null;
  const displayName = username || "Nestos Shop";
  const isAdmin = role === "admin";
  const translations = useMemo(
    () => TRANSLATIONS[language] || TRANSLATIONS.en,
    [language]
  );
  const currentLanguageOption = useMemo(
    () => LANGUAGE_OPTIONS.find((option) => option.value === language) || LANGUAGE_OPTIONS[0],
    [language]
  );
  const currentLanguageLabel = currentLanguageOption.labels?.[language] || currentLanguageOption.labels.en;
  const languageButtonLabel = `${translations.language}: ${currentLanguageLabel}`;

    return (
    <header className="fixed inset-x-0 top-0 z-40 border-b border-border/60 bg-background/85 backdrop-blur-xl">
      <div className="mx-auto flex w-full max-w-6xl flex-wrap items-center gap-3 px-4 py-3 sm:px-6 lg:px-8">
        <NavLink to="/" className="flex items-center gap-2 text-lg font-semibold">
          <img src="/nestos_invoice_logo.png" alt="Nestos" className="h-9 w-auto" />
        </NavLink>

        <nav className="hidden items-center justify-start gap-1 rounded-full border border-border/70 bg-card/70 p-1 text-sm font-medium shadow-sm md:flex">
          {NAV_ITEMS.map(({ key, to, icon: Icon }) => (
            <NavLink
              key={key}
              to={to}
              className={({ isActive }) =>
                cn(
                  "inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 transition-colors",
                  isActive ? "bg-background text-foreground shadow" : "text-muted-foreground hover:text-foreground"
                )
              }
            >
              <Icon className="h-4 w-4" />
              <span>{translations.nav[key]}</span>
              {key === "orders" && (
                <Badge variant="muted" className="ml-1 hidden uppercase text-[10px] tracking-wide text-muted-foreground lg:inline-flex">
                  {translations.beta}
                </Badge>
              )}
                </NavLink>
          ))}
        </nav>

        <div className="ml-auto flex items-center gap-3">
          {hasMarketplaces && (
            <Select
              value={selectedMarketplace || marketplaceOptions[0]?.country_code || ""}
              onValueChange={(value) => {
                setSelectedMarketplace?.(value);
              }}
            >
              <SelectTrigger className="hidden min-w-[200px] sm:inline-flex">
                <SelectValue placeholder={translations.selectMarketplace} />
              </SelectTrigger>
              <SelectContent>
                {marketplaceOptions.map((marketplace) => (
                  <SelectItem key={marketplace.country_code} value={marketplace.country_code}>
                    <div className="flex flex-col">
                      <span className="text-sm font-medium">{marketplace.name}</span>
                      <span className="text-xs text-muted-foreground">{marketplace.country_code}</span>
                </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="flex h-9 w-9 items-center justify-center rounded-full p-0 text-lg"
                aria-label={languageButtonLabel}
                title={languageButtonLabel}
              >
                <span aria-hidden="true">{currentLanguageOption.flag}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-40">
              <DropdownMenuLabel>{translations.language}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {LANGUAGE_OPTIONS.map((option) => (
                <DropdownMenuItem
                  key={option.value}
                  onSelect={(event) => {
                    event.preventDefault();
                    if (option.value !== language) {
                      onLanguageChange?.(option.value);
                    }
                  }}
                  className={cn(
                    "flex items-center gap-2 text-sm",
                    language === option.value ? "font-semibold text-foreground" : ""
                  )}
                >
                  <span aria-hidden="true">{option.flag}</span>
                  <span>{option.labels?.[language] || option.labels.en}</span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="flex items-center gap-3 rounded-full px-2">
                <Avatar className="h-9 w-9">
                  <AvatarImage src={`https://ui-avatars.com/api/?name=${encodeURIComponent(displayName)}&background=ff6a00&color=fff`} alt={displayName} />
                  <AvatarFallback>{displayName.slice(0, 2).toUpperCase()}</AvatarFallback>
                </Avatar>
                <div className="hidden text-left text-sm font-medium leading-tight sm:block">
                  <div>{displayName}</div>
                  <div className="text-xs text-muted-foreground">
                    {currentMarketplace
                      ? `${translations.currentMarketplacePrefix} ${currentMarketplace.country_code}`
                      : translations.noMarketplaceSelected}
                  </div>
                </div>
                <ChevronDown className="hidden h-4 w-4 text-muted-foreground sm:block" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <DropdownMenuLabel>{translations.account}</DropdownMenuLabel>
              <DropdownMenuItem
                className="pointer-events-none flex flex-col items-start gap-1 text-sm"
                onSelect={(event) => event.preventDefault()}
              >
                <span className="font-semibold text-foreground">Nestos Shop Invoice</span>
                <span className="text-xs text-muted-foreground">
                  {extensionAvailable ? translations.chromeConnected : translations.chromeDisconnected}
                </span>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuLabel>{translations.connectedMarketplaces}</DropdownMenuLabel>
              <DropdownMenuGroup>
                {hasMarketplaces ? (
                  marketplaceOptions.map((marketplace) => (
                    <DropdownMenuItem
                      key={marketplace.country_code}
                      onSelect={(event) => {
                        event.preventDefault();
                        setSelectedMarketplace?.(marketplace.country_code);
                      }}
                    >
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-foreground">{marketplace.name}</span>
                        <span className="text-xs text-muted-foreground">{marketplace.country_code}</span>
            </div>
                    </DropdownMenuItem>
                  ))
                ) : (
                  <DropdownMenuItem disabled className="flex flex-col items-start text-sm text-muted-foreground">
                    <span>{translations.noMarketplaces}</span>
                  </DropdownMenuItem>
                )}
              </DropdownMenuGroup>
              {isAdmin && (
                <DropdownMenuItem
                  onSelect={(event) => {
                    event.preventDefault();
                    navigate("/settings");
                  }}
                >
                  {translations.settings}
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                onSelect={(event) => {
                  event.preventDefault();
                  navigate("/marketplaces");
                }}
              >
                {translations.connectMarketplaces}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="text-destructive" onSelect={handleLogout}>
                <LogOut className="mr-2 h-4 w-4" />
                {translations.logOut}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
    );
};

export default Topbar;